import type { Client } from '@/types';

export type ClientReadinessDimensionKey =
  | 'sources'
  | 'style'
  | 'contacts'
  | 'campaigns'
  | 'crisis';

export type ClientReadinessStatus = 'missing' | 'weak' | 'ready';

export type ClientReadinessDimension = {
  key: ClientReadinessDimensionKey;
  label: string;
  status: ClientReadinessStatus;
  count: number;
  detail: string;
  missingReason?: string;
};

export type ClientContextReadiness = {
  mode: 'general' | 'client';
  clientId?: string;
  clientName?: string;
  industry?: string;
  level: 'general' | 'weak' | 'partial' | 'ready';
  score: number;
  summary: string;
  warnings: string[];
  dimensions: ClientReadinessDimension[];
  counts: {
    keyMessages: number;
    approvedLanguage: number;
    forbiddenLanguage: number;
    relevantDocuments: number;
    currentCampaigns: number;
    activeCampaigns: number;
    mediaContacts: number;
  };
};

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function asStringArray(value: unknown): string[] {
  return asArray(value)
    .map((item) => asString(item))
    .filter(Boolean);
}

function hasObjectSignal(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  if (Array.isArray(value)) {
    return value.some((entry) => {
      if (typeof entry === 'string') return !!entry.trim();
      return hasObjectSignal(entry);
    });
  }
  return Object.values(value as Record<string, unknown>).some((entry) => {
    if (typeof entry === 'string') return !!entry.trim();
    if (typeof entry === 'number' || typeof entry === 'boolean') return true;
    return hasObjectSignal(entry);
  });
}

function plural(count: number, singular: string, pluralLabel = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : pluralLabel}`;
}

function statusFromCount(count: number, readyAt = 1): ClientReadinessStatus {
  if (count >= readyAt) return 'ready';
  if (count > 0) return 'weak';
  return 'missing';
}

function countActiveCampaigns(campaigns: unknown[]): number {
  return campaigns.filter((campaign) => {
    if (!campaign || typeof campaign !== 'object') return false;
    const status = asString((campaign as Record<string, unknown>).status).toLowerCase();
    return status === 'active';
  }).length;
}

export function getClientContextReadiness(client: Client | null | undefined): ClientContextReadiness {
  if (!client) {
    return {
      mode: 'general',
      level: 'general',
      score: 0,
      summary: 'General mode',
      warnings: ['General mode will not apply client-specific sources, style, contacts, campaigns, or crisis protocol.'],
      dimensions: [
        {
          key: 'sources',
          label: 'Sources',
          status: 'missing',
          count: 0,
          detail: 'No client selected',
          missingReason: 'Select a client for account-specific context.',
        },
        {
          key: 'style',
          label: 'Style/preferences',
          status: 'missing',
          count: 0,
          detail: 'No client preferences',
          missingReason: 'Select a client for tone and language preferences.',
        },
        {
          key: 'contacts',
          label: 'Contacts',
          status: 'missing',
          count: 0,
          detail: 'No client contacts',
          missingReason: 'Select a client for media contacts.',
        },
        {
          key: 'campaigns',
          label: 'Campaigns',
          status: 'missing',
          count: 0,
          detail: 'No campaign context',
          missingReason: 'Select a client for current campaigns.',
        },
        {
          key: 'crisis',
          label: 'Crisis',
          status: 'missing',
          count: 0,
          detail: 'No crisis protocol',
          missingReason: 'Select a client for crisis readiness.',
        },
      ],
      counts: {
        keyMessages: 0,
        approvedLanguage: 0,
        forbiddenLanguage: 0,
        relevantDocuments: 0,
        currentCampaigns: 0,
        activeCampaigns: 0,
        mediaContacts: 0,
      },
    };
  }

  const context = (client.context || {}) as unknown as Record<string, unknown>;
  const industry = asString(client.industry);
  const keyMessages = asStringArray(context.keyMessages);
  const approvedLanguage = asStringArray(context.approvedLanguage);
  const forbiddenLanguage = asStringArray(context.forbiddenLanguage);
  const relevantDocuments = asArray(context.relevantDocuments);
  const currentCampaigns = asArray(context.currentCampaigns);
  const mediaContacts = asArray(context.mediaContacts);
  const crisisProtocol = asString(context.crisisProtocol);
  const hasCrisisPlanDocument = relevantDocuments.some((doc) => {
    if (!doc || typeof doc !== 'object') return false;
    const type = asString((doc as Record<string, unknown>).type).toLowerCase();
    return type === 'crisis_plan';
  });
  const activeCampaigns = countActiveCampaigns(currentCampaigns);
  const hasToneOrStyle =
    !!asString(context.brandTone)
    || hasObjectSignal(context.pressReleaseStyle)
    || hasObjectSignal(context.tonePreferences)
    || hasObjectSignal(context.styleProfile);

  const sourceCount = keyMessages.length + relevantDocuments.length + (industry ? 1 : 0);
  const styleCount =
    approvedLanguage.length
    + forbiddenLanguage.length
    + (hasToneOrStyle ? 1 : 0);
  const crisisCount = crisisProtocol || hasCrisisPlanDocument ? 1 : 0;

  const dimensions: ClientReadinessDimension[] = [
    {
      key: 'sources',
      label: 'Sources',
      status: statusFromCount(sourceCount, 2),
      count: sourceCount,
      detail: [
        industry ? 'industry set' : '',
        keyMessages.length ? plural(keyMessages.length, 'key message') : '',
        relevantDocuments.length ? plural(relevantDocuments.length, 'document') : '',
      ].filter(Boolean).join(', ') || 'No source context',
      missingReason: sourceCount < 2 ? 'Add key messages or relevant documents.' : undefined,
    },
    {
      key: 'style',
      label: 'Style/preferences',
      status: statusFromCount(styleCount, 1),
      count: styleCount,
      detail: [
        hasToneOrStyle ? 'tone/style profile' : '',
        approvedLanguage.length ? plural(approvedLanguage.length, 'approved term') : '',
        forbiddenLanguage.length ? plural(forbiddenLanguage.length, 'forbidden term') : '',
      ].filter(Boolean).join(', ') || 'No style preferences',
      missingReason: styleCount === 0 ? 'Add tone/style fields or approved and forbidden language.' : undefined,
    },
    {
      key: 'contacts',
      label: 'Contacts',
      status: statusFromCount(mediaContacts.length, 1),
      count: mediaContacts.length,
      detail: mediaContacts.length ? plural(mediaContacts.length, 'media contact') : 'No media contacts',
      missingReason: mediaContacts.length === 0 ? 'Add media contacts for pitch and outreach work.' : undefined,
    },
    {
      key: 'campaigns',
      label: 'Campaigns',
      status: statusFromCount(currentCampaigns.length, 1),
      count: currentCampaigns.length,
      detail: currentCampaigns.length
        ? `${plural(currentCampaigns.length, 'campaign')}${activeCampaigns ? `, ${activeCampaigns} active` : ''}`
        : 'No current campaigns',
      missingReason: currentCampaigns.length === 0 ? 'Add current campaigns for campaign-specific drafts.' : undefined,
    },
    {
      key: 'crisis',
      label: 'Crisis',
      status: statusFromCount(crisisCount, 1),
      count: crisisCount,
      detail: crisisProtocol
        ? 'protocol set'
        : hasCrisisPlanDocument
          ? 'crisis plan document'
          : 'No crisis protocol',
      missingReason: crisisCount === 0 ? 'Add a crisis protocol before crisis response work.' : undefined,
    },
  ];

  const readyCount = dimensions.filter((dimension) => dimension.status === 'ready').length;
  const weakCount = dimensions.filter((dimension) => dimension.status === 'weak').length;
  const score = readyCount * 2 + weakCount;
  const warnings = dimensions
    .filter((dimension) => dimension.status !== 'ready' && dimension.missingReason)
    .map((dimension) => dimension.missingReason as string);
  const level =
    readyCount >= 4
      ? 'ready'
      : readyCount >= 2 || score >= 4
        ? 'partial'
        : 'weak';

  return {
    mode: 'client',
    clientId: client.id,
    clientName: client.name,
    industry: industry || undefined,
    level,
    score,
    summary:
      level === 'ready'
        ? 'Context ready'
        : level === 'partial'
          ? 'Partial context'
          : 'Weak context',
    warnings,
    dimensions,
    counts: {
      keyMessages: keyMessages.length,
      approvedLanguage: approvedLanguage.length,
      forbiddenLanguage: forbiddenLanguage.length,
      relevantDocuments: relevantDocuments.length,
      currentCampaigns: currentCampaigns.length,
      activeCampaigns,
      mediaContacts: mediaContacts.length,
    },
  };
}
